import { getMeeting } from '@/api/meetings';
import { Box, Button, Container, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { PresentationChart as PresentationChartIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { Meeting } from '@/types/meeting';
import { paths } from '@/paths';
import { Seo } from '@/components/seo';

import { MeetingDescription, MeetingNotes, MeetingSidebar } from './components/meeting-details-card';
import { MeetingHeader } from './components/meeting-header';

export default function MeetingPage() {
  const theme = useTheme();
  const { meetingId } = useParams<{ meetingId: string }>();

  const { data: meeting, isLoading, error, refetch } = useQuery<Meeting | null>({
    queryKey: ['meeting', meetingId],
    queryFn: () => (meetingId ? getMeeting(meetingId) : Promise.resolve(null)),
    enabled: Boolean(meetingId),
    retry: false,
  });

  if (!meetingId) {
    return (
      <>
        <Seo title="Meeting Not Found" />
        <Box component="main" sx={{ flexGrow: 1, py: 3, bgcolor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center' }}>
              <PresentationChartIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Meeting not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This meeting doesn't exist or you don't have access to it.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">
                  Go Back
                </Button>
                <Button href={paths.meetings.index} variant="contained">
                  All Meetings
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  if (!isLoading && (error || !meeting)) {
    return (
      <>
        <Seo title="Meeting Not Found" />
        <Box component="main" sx={{ flexGrow: 1, py: 3, bgcolor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center' }}>
              <PresentationChartIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Meeting not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This meeting doesn't exist or you don't have access to it.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">
                  Go Back
                </Button>
                <Button href={paths.meetings.index} variant="contained">
                  All Meetings
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title={meeting?.meeting_type?.name ?? 'Meeting'} />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        {/* Header strip */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <MeetingHeader error={error} isLoading={isLoading} meeting={meeting} />
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            {/* Left column: agenda + notes */}
            <Grid md={8} xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                <Box sx={{ p: 3 }}>
                  <MeetingDescription meeting={meeting} isLoading={isLoading} />
                </Box>

                <Divider />
                <Box sx={{ p: 3 }}>
                  <MeetingNotes meeting={meeting ?? null} />
                </Box>
              </Paper>
            </Grid>

            {/* Right column: sidebar */}
            <Grid md={4} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: { md: 'sticky' },
                  top: { md: 24 },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <MeetingSidebar meeting={meeting} isLoading={isLoading} onRefetch={() => refetch()} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
